"use client";

import { layoutScrollTracker, virtualPageviewTracker } from "@containers/Layout/layoutTracker";
import { checkWebPSupport } from "@utils/imageHelpers/webPHelper";
import { getIsCoveredByInsurance } from "@utils/layoutHelpers/layoutHelper";
import { getPersonalInfoByAudienceId } from "@utils/targetGroup/getPersonalInfo";
import { getRoleById, getSectorById } from "@utils/targetGroup/targetGroupHelper";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import Cookies from "universal-cookie";

import { CookieConsentCategory, useCookieConsent } from "hooks/useCookieConsent";
import { PageInfo, usePageInfo } from "hooks/zustand/usePageInfo";
import { ISectorToggler, useSectorToggler } from "hooks/zustand/useSectorToggle";

const cookie = new Cookies();

type TrackingPageInfoProps = {
  pageName: string;
  firstLevelPageName: string;
  secondLevelPageName: string;
  insuranceName: string;
  insuranceCoveredTargetGroups: string[] | string;
  is404: boolean;
};

export function TrackingPageInfoUpdater({
  pageName,
  firstLevelPageName,
  secondLevelPageName,
  insuranceName,
  insuranceCoveredTargetGroups,
  is404,
}: TrackingPageInfoProps) {
  const setSelectedSector = useSectorToggler((state: ISectorToggler) => state.setSelectedSector);
  const setPageInfo = usePageInfo((state: PageInfo) => state.setPageInfo);
  const pathname = usePathname();

  useEffect(() => {
    const audienceId = cookie.get("Personalize");
    if (!audienceId && pathname.startsWith("/forsakring")) {
      setSelectedSector(null);
    } else {
      setSelectedSector(getPersonalInfoByAudienceId(audienceId));
    }
    const coveredTargetGroups = Array.isArray(insuranceCoveredTargetGroups)
      ? insuranceCoveredTargetGroups
      : [];
    const isCoveredByInsurance = getIsCoveredByInsurance(audienceId, coveredTargetGroups);

    setPageInfo({
      pageName: is404 ? "404" : pageName,
      firstLevelPageName: firstLevelPageName?.toLowerCase(),
      secondLevelPageName: secondLevelPageName?.toLowerCase(),
      pageRefferer: typeof document !== "undefined" ? document.referrer || "" : "",
      isCoveredByInsurance: isCoveredByInsurance,
      insuranceName: insuranceName?.toLowerCase(),
      currentPath: pathname,
      insuranceCoveredTargetGroups: coveredTargetGroups,
    });
  }, [
    pathname,
    pageName,
    firstLevelPageName,
    secondLevelPageName,
    insuranceName,
    insuranceCoveredTargetGroups,
    is404,
    setPageInfo,
    setSelectedSector,
  ]);

  return null;
}

export default function Tracking() {
  const necessaryCookieConsentGiven = useCookieConsent(CookieConsentCategory.necessary);
  const previousUrlRef = useRef<string>("");
  const pendingReferrerRef = useRef<string>("");
  const initialDocumentReferrerRef = useRef<string>(
    typeof document !== "undefined" ? document.referrer || "" : "",
  );
  const previousPathRef = useRef<string>("");
  const lastTrackedPathRef = useRef<string>("");
  const lastTrackedAudienceIdRef = useRef<string>("");
  const initialPathRef = useRef<string>("");
  const selectedSector = useSectorToggler((state: ISectorToggler) => state.selectedSector);

  const pageInfo = usePageInfo((state: PageInfo) => state.pageInfo);
  const router = useRouter();

  const pathname = usePathname();

  if (!initialPathRef.current) {
    initialPathRef.current = pathname;
  }

  const normalizeTrackingPart = (value?: string): string =>
    (value || "").toLocaleLowerCase("sv-SE").trim();

  const normalizeInsuranceSelector = (value?: string): string | undefined => {
    const normalized = normalizeTrackingPart(value);
    if (!normalized) {
      return undefined;
    }

    // Keep explicit prefixed values as-is.
    if (normalized.includes(" - ")) {
      return normalized;
    }

    if (normalized === "sjukförsäkring" || normalized === "sjukforsakring") {
      return "ags - sjukförsäkring";
    }

    return normalized;
  };

  const getInsuranceSelectorFromPath = (path: string): string | undefined => {
    const parts = path.split("?")[0].split("#")[0].split("/").filter(Boolean);
    if (parts[0] !== "forsakring") {
      return undefined;
    }

    const insuranceSlug = parts[1] || "";
    const selectorBySlug: Record<string, string> = {
      sjukforsakring: "ags - sjukförsäkring",
    };

    return selectorBySlug[insuranceSlug] || undefined;
  };

  // page_category: always from CMS firstLevelPageName.
  // Root "/" is always "start". For all other pages, CMS is the source of truth
  // — URL slugs cannot reliably be mapped to category labels (e.g. "ohalsa-och-arbetsskador"
  // → "förebygga ohälsa och arbetsskador") so there is no URL fallback.
  const resolvedPageCategory =
    normalizeTrackingPart(pageInfo?.firstLevelPageName) || (pathname === "/" ? "start" : "");

  // page_subcategory: always from CMS secondLevelPageName.
  // No URL fallback — it produces wrong values for e.g. FAQ detail pages.
  const resolvedPageSubcategory = normalizeTrackingPart(pageInfo?.secondLevelPageName) || "";

  useEffect(() => {
    // Reset milestone state per page so scroll depth is tracked again after
    // client-side (soft) navigation, where this component does not unmount.
    const scrollTracker = { 25: false, 50: false, 75: false, 90: false };

    const handleScrollTracking = () => {
      const scrollY = window?.scrollY;
      const scrollableHeight = document?.documentElement?.scrollHeight - window?.innerHeight;

      if (scrollableHeight <= 0) {
        return;
      }

      const percentage = Math.round((scrollY / scrollableHeight) * 100);

      if (percentage >= 25 && scrollTracker[25] === false) {
        scrollTracker[25] = true;
        layoutScrollTracker(25);
      }

      if (percentage >= 50 && scrollTracker[50] === false) {
        scrollTracker[50] = true;
        layoutScrollTracker(50);
      }

      if (percentage >= 75 && scrollTracker[75] === false) {
        scrollTracker[75] = true;
        layoutScrollTracker(75);
      }

      if (percentage >= 90 && scrollTracker[90] === false) {
        scrollTracker[90] = true;
        layoutScrollTracker(90);
        window.removeEventListener("scroll", handleScrollTracking);
      }
    };

    const timeoutId = setTimeout(() => window.addEventListener("scroll", handleScrollTracking), 0);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScrollTracking);
    };
  }, [pathname]);

  useEffect(() => {
    if (!necessaryCookieConsentGiven) return;
    if (pageInfo?.currentPath !== pathname) return;

    const currentAudienceId = selectedSector?.audienceId ?? "";
    const pathChanged = lastTrackedPathRef.current !== pathname;
    const sectorChanged = currentAudienceId !== lastTrackedAudienceIdRef.current;
    if (!pathChanged && !sectorChanged) return;

    const resolvedPageTitle = pageInfo?.pageName || document.title || pathname || "okand-sida";

    const sector = getSectorById(selectedSector?.sector?.sectorId);
    const role = getRoleById(selectedSector?.role?.roleId);
    const isFirstTrackedPageview = lastTrackedPathRef.current === "";
    const isInitialPath = pathname === initialPathRef.current;
    const useDocumentReferrerForFirstPageview = isFirstTrackedPageview && isInitialPath;

    const pageReferrer = useDocumentReferrerForFirstPageview
      ? initialDocumentReferrerRef.current || pageInfo?.pageRefferer || document.referrer || ""
      : pendingReferrerRef.current || pageInfo?.pageRefferer || document.referrer || "";

    const insuranceSelector =
      normalizeInsuranceSelector(pageInfo?.insuranceName) || getInsuranceSelectorFromPath(pathname);
    const pagePersonalize = getIsCoveredByInsurance(
      currentAudienceId,
      pageInfo?.insuranceCoveredTargetGroups ?? [],
    );

    virtualPageviewTracker(
      role,
      sector,
      resolvedPageCategory,
      resolvedPageSubcategory,
      pageReferrer,
      resolvedPageTitle,
      insuranceSelector,
      pagePersonalize,
    );

    lastTrackedPathRef.current = pathname;
    lastTrackedAudienceIdRef.current = currentAudienceId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    necessaryCookieConsentGiven,
    pageInfo,
    pathname,
    resolvedPageCategory,
    resolvedPageSubcategory,
    selectedSector,
  ]);

  useEffect(() => {
    pendingReferrerRef.current = previousUrlRef.current;
    previousUrlRef.current = window.location.href;
  }, [pathname]);

  useEffect(() => {
    const onPathChangeEvent = new CustomEvent("onPathChangeEvent", {
      detail: {
        message: pathname,
      },
    });

    document.dispatchEvent(onPathChangeEvent);
  }, [pathname]);

  useEffect(() => {
    checkWebPSupport("lossy", (_, result) => {
      document.body.classList.add(`${result ? "webp" : "no-webp"}`);
    });
  }, [router]);

  // Open cookie consent modal on next page if first page
  // visited was /kakor, where it's not displayed.
  useEffect(() => {
    const previousPath = previousPathRef.current;

    if (
      !necessaryCookieConsentGiven &&
      !pathname.endsWith("/kakor") &&
      previousPath?.endsWith("/kakor")
    ) {
      global.cookieTractor?.openConsentSettings();
    }
  }, [pathname, necessaryCookieConsentGiven]);

  //hide hotjar button for specific pages
  useEffect(() => {
    document.body.classList.forEach((className) => {
      if (className.startsWith("/")) {
        document.body.classList.remove(className);
      }
    });

    document.body.classList.add(pathname);
  }, [pathname]);

  useEffect(() => {
    previousPathRef.current = pathname;
  }, [pathname]);
  return null;
}
